import { Container, Graphics, Sprite, type Spritesheet } from "pixi.js";
import { workerAction } from "./actionMotion.js";

/** Hand-placed activity at the front of each existing plot, not new production rules. */
export const VILLAGE_LIFE: Readonly<
  Record<string, { type: "work" | "deliver" | "banner" | "cloth" | "grain"; x: number; y: number }>
> = {
  iron_mine: { type: "work", x: 24, y: 15 },
  woodcutter: { type: "work", x: 20, y: 16 },
  clay_pit: { type: "work", x: 22, y: 14 },
  embassy: { type: "banner", x: 24, y: 5 },
  shrine: { type: "banner", x: -32, y: 9 },
  warehouse: { type: "deliver", x: 24, y: 12 },
  marketplace: { type: "deliver", x: 30, y: 10 },
  main_building: { type: "banner", x: -38, y: 9 },
  granary: { type: "deliver", x: 18, y: 11 },
  residence: { type: "cloth", x: 20, y: 8 },
  grain_farm: { type: "grain", x: 24, y: 14 },
  stable: { type: "deliver", x: 28, y: 12 },
  engineers_guild: { type: "work", x: 20, y: 10 },
  barracks: { type: "banner", x: -31, y: 7 },
  workshop: { type: "work", x: 20, y: 10 },
  rally_point: { type: "banner", x: 23, y: 4 },
  wall: { type: "banner", x: 23, y: 0 },
};

/** Reused per plot; no per-frame sprites, textures or timers. Parent owns disposal. */
export class VillageLife extends Container {
  private readonly marks = new Graphics();
  private actor: Sprite | null = null;

  constructor(
    readonly buildingId: string,
    private readonly offset: number
  ) {
    super();
    this.eventMode = "none";
    this.addChild(this.marks);
  }

  update(seconds: number, art: Spritesheet | null, reduced: boolean, building: boolean): void {
    this.visible = !building;
    if (building) return;
    const profile = VILLAGE_LIFE[this.buildingId];
    if (!profile) return;
    const { type, x, y } = profile;
    const t = reduced ? 0 : seconds;
    const wind = reduced ? 0 : Math.sin(t * 1.65 + this.offset) * 1.2;
    const g = this.marks;
    g.clear();
    if (type === "work" || type === "deliver") {
      // Short staggered shifts keep the village alive without every plot moving at once.
      const phase = (t + this.offset * 2.7) % 18;
      const alpha = reduced ? 0.65 : Math.max(0, Math.min(1, phase / 0.8, (7.2 - phase) / 0.8));
      const texture = art?.textures[workerAction(phase, type === "deliver", reduced)];
      if (texture) {
        if (!this.actor) {
          this.actor = new Sprite(texture);
          this.actor.scale.set(0.78);
          this.addChild(this.actor);
        }
        this.actor.texture = texture;
        this.actor.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
        this.actor.position.set(x, y);
        this.actor.alpha = alpha;
        g.ellipse(x, y, 4.5, 2).fill({ color: 0x233b2d, alpha: alpha * 0.2 });
        if (type === "work") {
          // A low stone/wood work surface in front of the craftsman.
          g.poly([x + 4, y - 2, x + 10, y - 5, x + 14, y - 3, x + 8, y]).fill({
            color: 0x9e845e,
            alpha,
          });
        }
      }
      return;
    }
    if (type === "grain") {
      for (let i = 0; i < 7; i++) {
        const dx = x + i * 3;
        const dy = y + (i % 2) * 2;
        g.moveTo(dx, dy)
          .quadraticCurveTo(dx + wind * 0.5, dy - 4, dx + wind, dy - 8)
          .stroke({ color: 0xada466, width: 0.9, alpha: 0.85 });
        g.ellipse(dx + wind, dy - 8, 1.1, 2.3).fill({ color: 0xc9b46c, alpha: 0.9 });
      }
      return;
    }
    const height = type === "banner" ? 27 : 16;
    g.moveTo(x, y)
      .lineTo(x, y - height)
      .stroke({ color: 0x846342, width: 1.3 });
    g.poly([
      x + 1,
      y - height + 2,
      x + 12,
      y - height + 4 + wind,
      x + 11,
      y - height + 14 + wind * 0.5,
      x + 1,
      y - height + 12,
    ]).fill({ color: type === "cloth" ? 0xd8c7a6 : 0x276b68 });
    g.moveTo(x + 2, y - height + 11)
      .lineTo(x + 10, y - height + 13 + wind * 0.5)
      .stroke({ color: 0xc8a967, width: 0.8 });
    if (type === "banner") g.circle(x, y - height, 1.5).fill({ color: 0xc8a967 });
  }
}
